import type * as React from "react";

import { useState, useMemo } from "react";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CirclePlus, Eye } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import DinamixSkeletonTable from "../skeleton/DinamixTableSkeleton";
// import { Input } from "@/components/ui/input";
import NumberFormatSelector from "../NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Utils
// import { S3_BUCKET_LINK } from "../constants";

interface TaxSummary {
  yearly: number;
  currentExec: number;
  currentPlan: number;
  currentDiff: number;
  currentDiffPct: number;
  lastExec: number;
  lastDiff: number;
  lastDiffPct: number | null;
  companyName: string;
  companyNameEng: string;
  companyProfileUrl: string;
  companyId: string;
  docFile: string;
}

export interface TaxObligationResponse {
  status: boolean;
  message: string;
  total: number;
  data: TaxSummary[];
}

interface CommonProps {
  data?: TaxObligationResponse | DividendResponse;
  onYearChange?: (yearly: string) => void;
  isloading?: boolean;
  yearly?: string;
}

interface DividendData {
  id: string;
  companyId: string;
  yearly: number;
  plannedDividend: number;
  totalPhase: number;
  actualDividend: number;
  phase: number;
  docFile: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DividendResponse {
  status: boolean;
  message: string;
  total: number;
  data: DividendData[];
}

interface CommonProps {
  data?: TaxObligationResponse | DividendResponse;
  onYearChange?: (yearly: string) => void;
  onStartYearChange?: (year: string) => void;
  onEndYearChange?: (year: string) => void;
  isloading?: boolean;
  yearly?: string;
  startYear?: number;
  endYear?: number;
}

export const TaxLiabilityPlan: React.FC<CommonProps> = ({
  data,
  onStartYearChange,
  onEndYearChange,
  isloading,
  startYear,
  endYear,
}) => {
  const navigate = useNavigate();
  const taxData = data as TaxObligationResponse;
  const [unit, setUnit] = useState("1");
  const currentYear = new Date().getFullYear();
  const { delegationPlan } = useAccessPermission();

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!startYear || startYear === 0) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [startYear, yearOptions]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between mb-2 shadow-none gap-x-2 bg-none">
        <div className="flex items-center gap-x-2">
          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
            <Select
              value={startYear?.toString()}
              onValueChange={onStartYearChange}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">ທັງໝົດ</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີສິ້ນສຸດ</Label>
            <Select
              value={endYear?.toString()}
              onValueChange={onEndYearChange}
              disabled={!startYear || startYear === 0}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                {endYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <NumberFormatSelector unit={unit} setUnit={setUnit} />
        </div>
        <div className="flex flex-col justify-end w-auto h-full p-0">
          {delegationPlan?.isWrite && (
            <Button
              className="flex items-center gap-2"
              onClick={() =>
                navigate({
                  to: "/report/tax-obligations/tax-liability-plan/add-new",
                })
              }
            >
              <CirclePlus size={16} /> ສ້າງແຜນມອບພັນທະອາກອນ
            </Button>
          )}
        </div>
      </div>
      {isloading ? (
        <DinamixSkeletonTable rows={8} columns={5} />
      ) : (
        <Table className="border">
          <TableHeader className="">
            <TableRow className="[&>*]:whitespace-nowrap">
              <TableHead className="w-[60px]">ລຳດັບ</TableHead>
              <TableHead>ປະຈໍາປີ</TableHead>
              <TableHead>ຕົວຈິງຂອງ ປີນີ້</TableHead>
              <TableHead>ແຜນການຂອງ ປີນີ້</TableHead>
              <TableHead>ປ່ຽນແປງທຽບໃສ່ແຜນ</TableHead>
              <TableHead>ທຽບໃສ່ແຜນຂອງປີນີ້</TableHead>
              <TableHead>ຕົວຈິງຂອງປີກ່ອນ</TableHead>
              <TableHead>ປ່ຽນແປງທຽບໃສ່ແຜນ</TableHead>
              <TableHead>ທຽບໃສ່ ຂອງປີກ່ອນ</TableHead>
              {delegationPlan?.isDownload && (
                <TableHead>ເອກະສານຕິດຄັດ</TableHead>
              )}
            </TableRow>
          </TableHeader>

          <TableBody className="[&>*]:whitespace-nowrap">
            {taxData?.data.map((item, index) => (
              <TableRow
                key={index}
                className="cursor-pointer"
                onClick={() => {
                  localStorage.setItem("yearly", String(item.yearly));
                  navigate({
                    to: "/report/tax-obligations/tax-liability-plan",
                  });
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <div className="flex text-center gap-x-2">
                    {" "}
                    <Eye size={16} color="#007AFF" />
                    {item.yearly}{" "}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentExec / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentPlan / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentDiff / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {item.currentDiffPct?.toFixed(2)}%
                </TableCell>
                <TableCell className="text-right">
                  {(item.lastExec / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.lastDiff / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {item.lastDiffPct?.toFixed(2) || "-"}%
                </TableCell>
                {delegationPlan?.isDownload && (
                  <TableCell className="text-blue-500 underline">
                    {item.docFile ? (
                      // <a
                      // 	href={S3_BUCKET_LINK + item.docFile}
                      // 	target="_blank"
                      // 	rel="noopener noreferrer"
                      // >
                      // 	ເບິ່ງເອກະສານ
                      // </a>
                      <FileHandleViewer
                        fileUrl={item.docFile}
                        showBtnPreview={true}
                        text="ເບິ່ງເອກະສານ"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export type { TaxObligationResponse, DividendResponse };
export const DividendPlan: React.FC<CommonProps> = ({
  data,
  onStartYearChange,
  onEndYearChange,
  isloading,
  startYear,
  endYear,
}) => {
  const navigate = useNavigate();
  const dividendData = data as DividendResponse;
  const [unit, setUnit] = useState("1");
  const currentYear = new Date().getFullYear();
  const { delegationPlan } = useAccessPermission();

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!startYear || startYear === 0) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [startYear, yearOptions]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between mb-2 shadow-none gap-x-2 bg-none">
        <div className="flex items-center gap-x-2">
          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
            <Select
              value={startYear?.toString()}
              onValueChange={onStartYearChange}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">ທັງໝົດ</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີສິ້ນສຸດ</Label>
            <Select
              value={endYear?.toString()}
              onValueChange={onEndYearChange}
              disabled={!startYear || startYear === 0}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                {endYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <NumberFormatSelector unit={unit} setUnit={setUnit} />
        </div>
        <div className="flex flex-col justify-end w-auto h-full p-0">
          {delegationPlan?.isUpdate && (
            <Button
              className="flex items-center gap-2"
              onClick={() =>
                navigate({
                  to: "/report/tax-obligations/dividend-plan/add-new",
                })
              }
            >
              <CirclePlus size={16} /> ສ້າງແຜນມອບເງິນປັນຜົນ
            </Button>
          )}
        </div>
      </div>
      {isloading ? (
        <DinamixSkeletonTable rows={8} columns={5} />
      ) : (
        <Table className="border">
          <TableHeader>
            <TableRow className="[&>*]:whitespace-nowrap [&>*]:text-center">
              <TableHead className="w-[60px]">ລຳດັບ</TableHead>
              <TableHead>ປະຈໍາປີ</TableHead>
              <TableHead>ແຜນມອບເງິນ</TableHead>
              <TableHead>ຈຳນວນງວດ</TableHead>
              <TableHead>ມອບຕົວຈິງ</TableHead>
              <TableHead>ຈຳນວນງວດຕົວຈິງ</TableHead>
              <TableHead>ຜິດດ່ຽງຈາກແຜນ</TableHead>
              <TableHead>ສົມທຽບແຜນ (%)</TableHead>
              {delegationPlan?.isDownload && (
                <TableHead>ເອກະສານຕິດຄັດ</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody className="[&>*]:whitespace-nowrap">
            {dividendData?.data.map((item, index) => (
              <TableRow
                key={item.id}
                className=" cursor-pointer  [&>*]:text-center"
                onClick={() => {
                  localStorage.setItem("divId", String(item.id));
                  navigate({
                    to: "/report/tax-obligations/dividend-plan",
                  });
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.yearly} </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-2">
                    <Eye size={16} color="#007AFF" />{" "}
                    {(item?.plannedDividend
                      ? item?.plannedDividend / Number(unit)
                      : 0
                    ).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {item.totalPhase || 0}
                </TableCell>
                <TableCell className="text-right">
                  {(item.actualDividend
                    ? item.actualDividend / Number(unit)
                    : 0
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-center">{item.phase || 0}</TableCell>
                <TableCell className="text-right">
                  {(() => {
                    const difference =
                      item?.actualDividend - item.plannedDividend
                        ? (item?.actualDividend - item.plannedDividend) /
                          Number(unit)
                        : 0;

                    const isPositive = difference > 0;

                    return (
                      <span
                        className={`font-medium ${
                          isPositive ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {difference.toLocaleString()}
                      </span>
                    );
                  })()}
                </TableCell>

                <TableCell>
                  {(() => {
                    const percentage =
                      item.plannedDividend > 0
                        ? (item.actualDividend / item.plannedDividend) * 100
                        : 0;

                    const isOverPlan = percentage >= 100;

                    return (
                      <span
                        className={`font-medium ${
                          isOverPlan ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {percentage.toFixed(2)}%
                      </span>
                    );
                  })()}
                </TableCell>
                {delegationPlan?.isDownload && (
                  <TableCell className="text-blue-500 underline">
                    {item.docFile ? (
                      // <a
                      //   href={S3_BUCKET_LINK + item.docFile}
                      //   target="_blank"
                      //   rel="noopener noreferrer"
                      // >
                      //   ເບິ່ງເອກະສານ
                      // </a>
                      <FileHandleViewer
                        fileUrl={item.docFile}
                        showBtnPreview={true}
                        text="ເບິ່ງເອກະສານ"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};
